// PeriodFilter — segmented time-period control (1M / 3M / 1Y / All). Pure
// controlled component; pair with the useFilterLoading hook to shimmer on
// change. ARIA tablist. From the prototype.
//
// `disabled` periods render INERT — visible but muted, non-clickable, out of the
// tab order — never removed: a short-history account has nothing to show for 5Y,
// and users should see the option exists rather than wonder if it's a bug
// (design: dashboard-redesign.jsx PeriodFilter, FM-2116).
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { cx } from '../../../utils/cx';
import './period-filter.css';

export function PeriodFilter({ options, value, onChange, ariaLabel, className, disabled }) {
  const { t } = useTranslation();
  const inert = new Set(disabled || []);
  return (
    <div className={cx('period-filter', className)} role="tablist" aria-label={ariaLabel ?? t('reusable.periodFilter.aria')}>
      {options.map((opt) => {
        const isDisabled = inert.has(opt);
        return (
          <button
            key={opt}
            type="button"
            role="tab"
            aria-selected={value === opt}
            aria-disabled={isDisabled || undefined}
            disabled={isDisabled}
            tabIndex={isDisabled ? -1 : undefined}
            className={cx('period-filter__btn', value === opt && 'is-active', isDisabled && 'is-disabled')}
            onClick={() => { if (!isDisabled) onChange(opt); }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

PeriodFilter.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  /** Period keys rendered muted + non-clickable (visible, never removed). */
  disabled: PropTypes.arrayOf(PropTypes.string),
};

export default PeriodFilter;
